/**
 * PROGÈRE — Client-side image compression
 * ==========================================
 * Compresses images before uploading to backend to:
 *   1. Reduce payload size (8 MB → ~300 KB typical)
 *   2. Speed up Claude Vision processing
 *   3. Stay under Cloudflare's 100-second timeout on production
 *
 * Uses Canvas API — works in all modern browsers.
 */

/**
 * Compress a File/Blob into a base64 data URL.
 * @param {File|Blob} file
 * @param {Object} opts
 * @param {number} opts.maxDim - Max width or height in pixels (default 1600)
 * @param {number} opts.quality - JPEG quality 0..1 (default 0.85)
 * @param {string} opts.mimeType - Output format (default 'image/jpeg')
 * @returns {Promise<{dataUrl: string, sizeKb: number, width: number, height: number}>}
 */
export async function compressImage(file, opts = {}) {
  const { maxDim = 1600, quality = 0.85, mimeType = "image/jpeg" } = opts;

  if (!file) throw new Error("No file provided");

  const bitmap = await createBitmap(file);
  const { width: srcW, height: srcH } = bitmap;

  const scale = Math.min(1, maxDim / Math.max(srcW, srcH));
  const w = Math.round(srcW * scale);
  const h = Math.round(srcH * scale);

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#000"; // preserve dark backgrounds when JPEG replaces transparency
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(bitmap, 0, 0, w, h);

  try { bitmap.close?.(); } catch { /* ignore */ }

  const dataUrl = canvas.toDataURL(mimeType, quality);
  const sizeKb = Math.round((dataUrl.length * 0.75) / 1024);
  return { dataUrl, sizeKb, width: w, height: h };
}

/**
 * Load a File/Blob into a decoded bitmap. Uses createImageBitmap when available
 * (faster, off-main-thread on modern browsers), falls back to <img>.
 */
async function createBitmap(file) {
  if (typeof createImageBitmap === "function") {
    try { return await createImageBitmap(file); }
    catch { /* Safari old versions — fall back */ }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = url;
    });
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
}
